<template>
    <div class="notifications-container">
        <transition-group name="notification-list" tag="div" class="notifications-list">
            <NotificationItem
                v-for="notification in notifications"
                :key="notification.id"
                :notification="notification"
                @close="handleClose"
                @click="handleClick"
            />
        </transition-group>
        <SegmentProposalModal
            v-if="client"
            ref="segmentProposalModalRef"
            :client="client"
        />
    </div>
</template>

<script setup>
import { ref, onBeforeUnmount, onMounted, computed, watch } from 'vue';
import { usePage } from '@inertiajs/vue3';
import { useNotifications } from '../../composables/useNotifications';
import { useClientStore } from '../../stores/client';
import NotificationItem from './NotificationItem.vue';
import SegmentProposalModal from '../pathways/SegmentProposalModal.vue';

const page = usePage();
const clientStore = useClientStore();
const notificationsComposable = useNotifications();
const { notifications, removeNotification, handleNotificationClick, showNotification, showProgressNotification } = notificationsComposable;

const segmentProposalModalRef = ref(null);
const client = computed(() => page.props.client || clientStore.client);
const clientId = computed(() => client.value?.id || null);

const AI_SUGGESTION_ACTION_TYPES = new Set([
    'openColumnMappingProposal',
    'openFileMappingProposal',
    'openFilterProposal',
    'openFileFilterProposal',
    'openSegmentProposal',
    'openReportPageFilterProposal',
]);

const PENDING_AI_PATTERNS = [
    "analyzing your",
    "analyzing your request",
    "creating your audience",
    "refining your segment",
    "suggest column mappings",
    "suggest a filter",
];

function dismissPendingAINotifications() {
    const toRemove = notifications.value.filter(n => {
        if (!n.text) return false;
        const lower = n.text.toLowerCase();
        return PENDING_AI_PATTERNS.some(p => lower.includes(p));
    });
    toRemove.forEach(n => removeNotification(n.id));
}

function handleOpenSegmentProposal(e) {
    const id = e.detail?.segment_suggestion_id;
    if (id != null && client.value && segmentProposalModalRef.value) {
        segmentProposalModalRef.value.show(id);
    }
}

let echoChannel = null;
let echoChannelName = null;

function startListening(id) {
    if (!window.Echo || !id) {
        return;
    }

    echoChannelName = `client.${id}`;
    echoChannel = window.Echo
        .private(echoChannelName)
            .listen('Notification', (event) => {
                if (event.action?.type && AI_SUGGESTION_ACTION_TYPES.has(event.action.type)) {
                    dismissPendingAINotifications();
                }
                showNotification({
                    icon: event.icon,
                    text: event.text,
                    link: event.link || null,
                    externalLink: event.externalLink || null,
                    noDismiss: event.noDismiss || false,
                    action: event.action || null,
                });
            })
            .listen('ProgressNotification', (event) => {
                showProgressNotification({
                    notificationId: event.notificationId,
                    icon: event.icon,
                    text: event.text,
                    percentage: event.percentage,
                    status: event.status,
                    link: event.link || null,
                    externalLink: event.externalLink || null,
                });
            });
    }

function stopListening() {
    if (!window.Echo || !echoChannelName) {
        echoChannel = null;
        echoChannelName = null;
        return;
    }

    try {
        window.Echo.leave(echoChannelName);
    } catch (e) {
        // Channel might not exist, silently continue
    }

    echoChannel = null;
    echoChannelName = null;
}

watch(
    clientId,
    (newClientId, oldClientId) => {
        if (!newClientId && !oldClientId) {
            return;
        }

        stopListening();
        startListening(newClientId);
    },
    { immediate: true }
);

onMounted(() => {
    window.addEventListener('openSegmentProposal', handleOpenSegmentProposal);
});

onBeforeUnmount(() => {
    window.removeEventListener('openSegmentProposal', handleOpenSegmentProposal);
    stopListening();
});

function handleClose(notificationId) {
    removeNotification(notificationId);
}

function handleClick(notification) {
    handleNotificationClick(notification);
}
</script>

